import React from 'react';
import { Button, Avatar } from 'antd';
import { ChevronDown, Shell } from 'lucide-react';

const NavLink = ({ children, active }) => (
  <span
    className={`text-sm cursor-pointer px-3 py-2 hover:bg-black/5 ${
      active ? 'font-medium' : ''
    }`}
  >
    {children}
    <ChevronDown className="inline ml-1" size={14} />
  </span>
);

const UserAvatars = ({ avatarUrls = [], text, maxAvatars = 5 }) => (
  <div className="flex flex-col items-center mt-8">
    <div className="flex -space-x-2">
      {avatarUrls.slice(0, maxAvatars).map((url, i) => (
        <Avatar
          src={url}
          size={64}
          key={i}
          className="w-12 h-12 rounded-full border-2 border-white bg-white"
        />
      ))}
      {avatarUrls.length > maxAvatars && (
        <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-900 flex items-center justify-center text-white text-xs">
          +{avatarUrls.length - maxAvatars}
        </div>
      )}
    </div>
    {text && <p className="text-sm text-gray-500 mt-3">{text}</p>}
  </div>
);

const GradientBanner = ({
  topText = '',
  topChildren = null,
  topTextLink = '',
  title = 'Website design system and UI Kit',
  subtitle = '',
  subtext = '',
  description = '',
  filledButtonText,
  filledButtonOnClick = () => {},
  outlinedButtonText,
  outlinedButtonOnClick = () => {},
  avatarText,
  avatarUrls,
  gradientClassName = 'bg-gradient-to-b from-rose-100 via-teal-50 to-amber-100',
  className = '',
  children,
}) => {
  return (
    <div
      className={`w-full ${gradientClassName} ${className}`}
    >
      <div className="mx-auto px-4 py-6 flex flex-col items-center text-center">
        {/* Top text with link */}
        <div className="mb-4 flex items-center space-x-2 text-sm">
          {topChildren}
          <span className="text-gray-600">{topText}</span>
          <span className="text-gray-600 hover:underline cursor-pointer">
            {topTextLink}
          </span>
        </div>

        {/* Main title and subtitle */}
        <span className="text-3xl md:text-5xl font-normal mb-2" style={{fontFamily: "SF Pro Display, sans-serif", textShadow: '0 1px 2px rgba(0,0,0,0.5)'}}>{title}</span>
        <h2 className="text-base md:text-lg text-gray-600 mb-2">{subtitle}</h2>

        {/* Description */}
        <p className="text-gray-600 mb-4 max-w-xl text-sm md:text-base">{description}</p>

        {/* Buttons */}
        {(outlinedButtonText || filledButtonText) && (
          <div className="flex flex-wrap gap-4 justify-center">
            {filledButtonText && (
              <Button
                onClick={filledButtonOnClick}
                className="bg-gray-900 text-white border-none h-10 px-6 flex items-center space-x-2"
                size="large"
              >
                <Shell />
                <span>{filledButtonText}</span>
              </Button>
            )}
            {outlinedButtonText && (
              <Button
                onClick={outlinedButtonOnClick}
                type="default"
                size="large"
                className="h-10 px-6"
              >
                {outlinedButtonText}
              </Button>
            )}
          </div>
        )}

        {/* Subtext */}
        <p className="text-xs text-gray-400 mt-4">{subtext}</p>

        {/* User avatars */}
        {Array.isArray(avatarUrls) && avatarUrls.length > 0 && (
            <UserAvatars avatarUrls={avatarUrls} text={avatarText} maxAvatars={5} />

        )}
        {children}
      </div>
    </div>
  );
};

export default GradientBanner;